import axiosInstance from "./api";
import TokenService, { isDevToken } from "./token.service";
import AuthService from "../services/auth.service";

/*
 * The bodies /auth/refresh returns when the session is genuinely finished.
 *
 * "Token could not be found, please relog" was MISSING here, and it is what the
 * backend actually sends for a revoked token and for a deleted account — so
 * those two cases never dispatched `sessionExpired` at all. The user sat on a
 * dead session until something else happened to sign them out.
 *
 * "Token was blacklisted, please relog" is kept even though the current backend
 * no longer emits it: an older deployment still might, and a stale entry costs
 * nothing while a missing one costs a stuck session.
 */
const UNREFRESHABLE_ERRORS = new Set([
  "Token has expired and can no longer be refreshed, please relog",
  "Token could not be found, please relog",
  "Token was blacklisted, please relog",
]);

/*
 * 401 is the correct status for "this token is finished" and is what the
 * backend now returns. 500 is kept deliberately: the DEPLOYED backend still
 * answers 500, and this bundle has to work against both during a rollout.
 * Dropping it would strand every already-running tab on the old API.
 *
 * Safe against recursion: AuthService.refresh() uses a bare axios instance, so
 * a 401 from the refresh call itself never re-enters this interceptor.
 */
const isUnrefreshable = (response) =>
  (response?.status === 401 || response?.status === 500) &&
  UNREFRESHABLE_ERRORS.has(response?.data?.error);

// Shared in-flight refresh: concurrent failures await the same request.
let refreshPromise = null;

const refreshToken = (store) => {
  if (!refreshPromise) {
    refreshPromise = AuthService.refresh()
      .catch((error) => {
        if (isUnrefreshable(error.response)) {
          store.dispatch("auth/sessionExpired");
        }
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

const setup = (store) => {
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = TokenService.getLocalAccessToken();
      if (token) {
        config.headers["Authorization"] = "Bearer " + token; // for Spring Boot back-end
        // config.headers["x-access-token"] = token; // for Node.js Express back-end
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    (res) => {
      return res;
    },
    async (err) => {
      const originalConfig = err?.config;

      /* Not every rejection carries a config: a cancelled/aborted request, a
       * failure raised by another interceptor, or a non-axios throw all arrive
       * here without one. Reading `.url` off that threw a TypeError, which then
       * replaced the real error — so the user saw the generic "Something went
       * wrong on this page" instead of the actual API message, and the failure
       * became unattributable in the console. Nothing without a config or a
       * response is retryable anyway, so bail out early. */
      if (!originalConfig || !err.response) {
        return Promise.reject(err);
      }

      if (originalConfig.url !== "/auth/signin") {
        // Access Token was expired
        if (
          (err.response.status === 500 || err.response.status === 401) &&
          !originalConfig._retry
        ) {
          const storedToken = TokenService.getLocalAccessToken();

          // No stored token => already logged out (e.g. the session-expired
          // flow already ran). Don't attempt a refresh, just fail.
          if (!storedToken) {
            return Promise.reject(err);
          }

          /* The localhost-only dev login (Login.vue) mints an unsigned JWT that
           * no backend accepts, so EVERY request it makes comes back 401 with
           * "Token could not be found, please relog" — an entry in
           * UNREFRESHABLE_ERRORS. That fired `sessionExpired` on the first API
           * call of the first page, which threw the dev account straight back
           * to the login screen with a "Session expired" modal.
           *
           * There is no session to refresh or to expire here: the token was
           * never real. Fail the request and leave the dev session standing, so
           * the panel can still be browsed for layout and empty states. */
          if (isDevToken(storedToken)) {
            return Promise.reject(err);
          }

          originalConfig._retry = true;

          try {
            await refreshToken(store);

            return axiosInstance(originalConfig);
          } catch (_error) {
            return Promise.reject(_error);
          }
        }
      }

      return Promise.reject(err);
    }
  );
};

export default setup;
